// src/pages/MilestonesPage.js
//
// The full milestones surface: your complete trophy case (earned AND not yet),
// then the community Wall.
//
// Reached from the Home shelf's "View all" — deliberately NOT a bottom-nav
// item. Mobile nav already carries four; a fifth would crowd the primary
// journeys for a surface members visit occasionally, not daily.
//
// Member-gated, matching the badge shelf: a lapsed member is redirected to
// Home rather than shown a wall they can no longer participate in.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import MilestoneBadge from './../components/MilestoneBadge';
import MilestoneWall from '../components/MilestoneWall';
import MilestoneDetail, { badgeButtonReset } from '../components/MilestoneDetail';
import { TapHint } from '../components/MilestoneShelf';
import CommunityCounter from '../components/CommunityCounter';
import { fetchUserCompletions, fetchSiteContent } from '../services/firebase';
import { computeMilestones, BADGES, bestRunNote } from '../services/milestones';
import { hasActiveSubscription } from '../services/classAccess';
import { fetchCommunityTotal } from '../services/communityStats';
import { displayNameFor } from '../services/milestoneWall';

function formatEarned(dayKey) {
    if (!dayKey) return '';
    const [y, m, d] = dayKey.split('-').map(Number);
    if (!y || !m || !d) return '';
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    return dt.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
        ...(dt.getUTCFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
        timeZone: 'UTC',
    });
}

function MilestonesPage() {
    const navigate = useNavigate();
    const [completions, setCompletions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [c, setC] = useState({});
    const [communityTotal, setCommunityTotal] = useState(null);
    const [openKey, setOpenKey] = useState(null);      // badge detail card
    const [hoveredKey, setHoveredKey] = useState(null);

    let user = null;
    let getToken = null;
    try { user = useUser().user; getToken = useAuth().getToken; } catch (e) { /* signed out */ }

    const userId = user?.id || '';
    const isAdmin = user?.publicMetadata?.role === 'admin';
    const isFriend = user?.publicMetadata?.role === 'friend';
    const isMember = isAdmin || isFriend || hasActiveSubscription(user);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            fetchUserCompletions(userId),
            fetchSiteContent(),
            fetchCommunityTotal(),
        ]).then(([comps, content, community]) => {
            if (cancelled) return;
            setCompletions(comps || []);
            setC(content || {});
            setCommunityTotal(community ? community.total : null);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [userId]);

    const milestones = useMemo(() => computeMilestones(completions), [completions]);

    // Non-members don't get a partial view — Home is their surface.
    useEffect(() => {
        if (!loading && user && !isMember) navigate('/dashboard');
    }, [loading, user, isMember, navigate]);

    const earnedByKey = useMemo(() => {
        const map = {};
        milestones.earned.forEach((b) => { map[b.key] = b; });
        return map;
    }, [milestones]);

    // Badges this member can see (a seasonal badge only during its window or
    // once earned) — drives the grid AND the "X of N earned" count.
    const visibleBadges = useMemo(() => {
        const keys = new Set(milestones.visibleKeys || []);
        return BADGES.filter((b) => keys.has(b.key));
    }, [milestones]);

    const unearnedByKey = useMemo(() => {
        const map = {};
        milestones.unearned.forEach((b) => { map[b.key] = b; });
        return map;
    }, [milestones]);

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh' }}>
            <AppNav />
            <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 80px' }}>

                <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 18,
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                        color: 'var(--mom-link, var(--mom-primary))',
                    }}
                >
                    ← {c.milestonesBackLabel || 'Back to home'}
                </button>

                <h1 style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 34,
                    fontWeight: 600, color: 'var(--mom-charcoal)',
                }}>
                    {c.milestonesPageHeading || 'Class Milestones'}
                </h1>
                <div style={{
                    width: 44, height: 2, background: 'var(--mom-primary)',
                    opacity: 0.45, borderRadius: 1, margin: '10px 0 20px',
                }} />
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 14.5, lineHeight: 1.7,
                    color: 'var(--mom-warm-gray)', maxWidth: '62ch',
                }}>
                    {c.milestonesPageSubtext || 'Every milestone you\'ve reached — and the ones still ahead.'}
                </p>

                {loading ? (
                    <div style={{ padding: '60px 0', textAlign: 'center' }}>
                        <div style={{
                            width: 28, height: 28, margin: '0 auto',
                            border: '2.5px solid var(--mom-cream-dark)',
                            borderTopColor: 'var(--mom-primary)', borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                        }} />
                    </div>
                ) : (
                    <>
                        {/* ── Your trophy case: every badge, earned or not ── */}
                        <div className="mom-card" style={{
                            borderRadius: 26, padding: '28px 26px',
                            marginTop: 26, marginBottom: 20,
                        }}>
                            <div style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                                color: 'var(--mom-warm-gray)', marginBottom: 22,
                            }}>
                                {milestones.earned.length} of {visibleBadges.length} earned
                                {' · '}
                                {milestones.classCount} {milestones.classCount === 1 ? 'class' : 'classes'} completed
                                <TapHint copy={c} style={{ margin: '6px 0 0' }} />
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(116px, 1fr))',
                                gap: 22,
                            }}>
                                {visibleBadges.map((b) => {
                                    const e = earnedByKey[b.key];
                                    const u = unearnedByKey[b.key];
                                    return (
                                        <button
                                            type="button"
                                            key={b.key}
                                            aria-label={`${b.name} — how it's earned`}
                                            onClick={() => setOpenKey(b.key)}
                                            onMouseEnter={() => setHoveredKey(b.key)}
                                            onMouseLeave={() => setHoveredKey(null)}
                                            style={{
                                                ...badgeButtonReset, display: 'block', width: '100%', padding: '6px 0',
                                                transform: hoveredKey === b.key ? 'translateY(-2px)' : 'none',
                                                transition: 'transform 0.2s ease',
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <MilestoneBadge
                                                    badgeKey={b.key}
                                                    size={88}
                                                    locked={!e}
                                                    progress={u ? u.progress : 0}
                                                />
                                            </div>
                                            <div style={{
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 11.5, fontWeight: 600,
                                                marginTop: 10, lineHeight: 1.35,
                                                color: e ? 'var(--mom-charcoal)' : 'var(--mom-warm-gray)',
                                            }}>
                                                {b.name}
                                            </div>
                                            <div style={{
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 10.5,
                                                color: 'var(--mom-warm-gray)', marginTop: 3,
                                            }}>
                                                {e ? formatEarned(e.earnedDayCT) : `${u ? u.current : 0} of ${b.threshold}`}
                                            </div>
                                            {!e && bestRunNote(u, c) && (
                                                <div style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 10,
                                                    color: 'var(--mom-warm-gray)', marginTop: 2, opacity: 0.85,
                                                }}>
                                                    {bestRunNote(u, c)}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <CommunityCounter total={communityTotal} copy={c} />

                        {/* ── The Wall ── */}
                        <div className="mom-card" style={{
                            borderRadius: 26, padding: '26px 28px',
                        }}>
                            <h2 style={{
                                fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 22,
                                fontWeight: 600, color: 'var(--mom-charcoal)',
                            }}>
                                {c.milestoneWallHeading || 'The Milestone Wall'}
                            </h2>
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 12.5,
                                color: 'var(--mom-warm-gray)', margin: '5px 0 8px',
                            }}>
                                {c.milestoneWallSubtext || 'Every milestone this community has reached. Cheer someone on.'}
                            </p>

                            <MilestoneWall
                                userId={userId}
                                displayName={displayNameFor(user) || 'A member'}
                                isAdmin={isAdmin}
                                emptyLabel={c.milestoneWallEmpty}
                                getToken={getToken}
                                viewerMilestones={milestones}
                                copy={c}
                            />
                        </div>
                    </>
                )}
            </div>

            {openKey && (
                <MilestoneDetail
                    badgeKey={openKey}
                    keys={visibleBadges.map((b) => b.key)}
                    milestones={milestones}
                    copy={c}
                    onNavigate={setOpenKey}
                    onClose={() => setOpenKey(null)}
                />
            )}
        </div>
    );
}

export default MilestonesPage;
